/**
 * Производственный календарь — карточки активных проектов (договоров).
 *
 * Авто-импорт из AmoCRM: сделки в стадии «Договор заключён» падают сюда
 * через /api/production/sync. После — менеджеры могут править поля карточки
 * (тех. детали, локация, даты, фактическая выручка). Удалить — только
 * вручную (после soft-delete карточка не воскресает при следующем sync).
 */
import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";
import {
  fetchProductionList, createProductionProject, updateProductionProject,
  deleteProductionProject, syncProductionFromAmo, applySyncDiffs,
  type ProductionProject, type ProductionPatch, type SyncPendingDiff,
} from "@/api/dashboard";
import { fmt } from "@/components/prototype/Avatar";
import { filterProjects } from "@/pages/SupportPage";


const MONTHS = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];
// 12 одиночных + 11 пар идущих подряд месяцев = 23 варианта
const PERIOD_OPTIONS: string[] = (() => {
  const out: string[] = [];
  for (let i = 0; i < MONTHS.length; i++) {
    out.push(MONTHS[i]);
    if (i < MONTHS.length - 1) {
      out.push(`${MONTHS[i]}-${MONTHS[i + 1].toLowerCase()}`);
    }
  }
  return out;
})();

const STATUS_OPTIONS: [string, string, string][] = [
  ["in_progress",  "В работе",       "var(--brick-50)"],
  ["completed",    "Выполнен",       "var(--green-soft)"],
  ["awaiting",     "В ожидании",     "var(--paper-2)"],
  // «Не заключился» (not_signed) — старый статус, в UI чипов больше не
  // показываем. Если такие карточки есть в БД — попадают в «В ожидании»
  // через fallback в KANBAN_COLUMNS.
];

function statusLabel(s: string): string {
  return STATUS_OPTIONS.find(o => o[0] === s)?.[1] ?? s;
}
function statusColor(s: string): string {
  return STATUS_OPTIONS.find(o => o[0] === s)?.[2] ?? "var(--paper-2)";
}

function paymentLabel(t: string | null | undefined): string {
  if (t === "cash") return "Наличный расчёт";
  if (t === "non_cash") return "Безналичный расчёт";
  return "Тип расчёта не указан";
}
/** Цикл null → cash → non_cash → null */
function nextPaymentType(t: string | null | undefined): string | null {
  if (!t) return "cash";
  if (t === "cash") return "non_cash";
  return null;
}


export function ProductionPage() {
  const qc = useQueryClient();
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterFromIso, setFilterFromIso] = useState<string>("");  // YYYY-MM-DD
  const [filterToIso, setFilterToIso] = useState<string>("");
  // По умолчанию ВЫКЛЮЧАЕМ старые сделки до сент.2025 без планирования
  // (они «исторический шум», портят статистику). Юзер может включить.
  const [includeOldUnplanned, setIncludeOldUnplanned] = useState<boolean>(false);
  // ?open={id} в URL — открыть конкретный проект (при переходе с P&L)
  const initialOpenId = (() => {
    if (typeof window === "undefined") return null;
    const p = new URLSearchParams(window.location.search);
    const v = p.get("open");
    return v ? Number(v) || null : null;
  })();
  const [expandedId, setExpandedId] = useState<number | null>(initialOpenId);
  const [creatingNew, setCreatingNew] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  // View mode: kanban (3 столбца) | rows (построчно)
  const [viewMode, setViewMode] = useState<"kanban" | "rows">(() => {
    if (typeof window === "undefined") return "kanban";
    const v = window.localStorage.getItem("kb-prod-view");
    return (v === "kanban" || v === "rows") ? v : "kanban";
  });
  function changeView(m: "kanban" | "rows") {
    setViewMode(m);
    try { window.localStorage.setItem("kb-prod-view", m); } catch { /* ignore */ }
  }

  const q = useQuery({
    queryKey: ["production", filterStatus],
    queryFn: () => fetchProductionList({
      status: filterStatus || undefined,
    }),
    refetchInterval: 30000,
  });

  const [syncDiffs, setSyncDiffs] = useState<SyncPendingDiff[] | null>(null);
  const syncMut = useMutation({
    mutationFn: () => syncProductionFromAmo(),
    onSuccess: (r) => {
      const summary = `Синхронизировано из AmoCRM:\n· создано: ${r.created}\n· уже было: ${r.skipped_existing}\n· пропущено (удалены вручную): ${r.skipped_deleted}\n· удалено дублей: ${r.deduped ?? 0}\n· отмечено выполненными (Полностью оплачено): ${r.completed_marked ?? 0}\n· всего сделок в Amo: ${r.total_deals_in_stage}`;
      const diffs = r.pending_diffs ?? [];
      qc.invalidateQueries({ queryKey: ["production"] });
      if (diffs.length > 0) setSyncDiffs(diffs);
      else alert(summary + `\n· изменений в Amo: 0`);
    },
    onError: (e: Error) => alert(`Ошибка синхронизации: ${e.message}`),
  });
  const applyDiffsMut = useMutation({
    mutationFn: applySyncDiffs,
    onSuccess: (r) => {
      setSyncDiffs(null);
      alert(`Применено: ${r.pp_updated} карточек pp, ${r.po_updated} объектов P&L`);
      qc.invalidateQueries({ queryKey: ["production"] });
      qc.invalidateQueries({ queryKey: ["finance-overview"] });
      qc.invalidateQueries({ queryKey: ["finance-objects"] });
    },
    onError: (e: Error) => alert(`Ошибка применения: ${e.message}`),
  });

  const createMut = useMutation({
    mutationFn: (data: ProductionPatch) => createProductionProject(data),
    onSuccess: () => {
      setCreatingNew(false);
      qc.invalidateQueries({ queryKey: ["production"] });
    },
    onError: (e: Error) => alert(`Ошибка создания: ${e.message}`),
  });

  const allItems = q.data?.items ?? [];

  // Парсим start-дату из work_dates "DD.MM.YYYY — DD.MM.YYYY"
  // (нужно и для фильтра, и для сортировки)
  const parseRange = (s: string | null | undefined): { start: Date; end: Date } | null => {
    if (!s) return null;
    const m = s.match(/(\d{2})\.(\d{2})\.(\d{4})\s*[—\-–]\s*(\d{2})\.(\d{2})\.(\d{4})/);
    if (!m) return null;
    return {
      start: new Date(`${m[3]}-${m[2]}-${m[1]}`),
      end: new Date(`${m[6]}-${m[5]}-${m[4]}`),
    };
  };

  // «Старая сделка без планирования» = lead до сент.2025 + не в Excel-планировании
  const isOldUnplanned = (p: ProductionProject): boolean =>
    !!p.lead_created_at && p.lead_created_at < "2025-09-01" && !p.in_planning;
  const oldUnplannedCount = useMemo(() =>
    allItems.filter(isOldUnplanned).length,
    [allItems]);

  // Фильтр по диапазону + по «старым без планирования» + сортировка
  const items = useMemo(() => {
    const fFrom = filterFromIso ? new Date(filterFromIso) : null;
    const fTo = filterToIso ? new Date(filterToIso) : null;
    let filtered = (filterFromIso || filterToIso)
      ? allItems.filter(p => {
          const r = parseRange(p.work_dates);
          if (!r) return false;
          if (fFrom && r.end < fFrom) return false;
          if (fTo && r.start > fTo) return false;
          return true;
        })
      : allItems.slice();
    if (!includeOldUnplanned) {
      filtered = filtered.filter(p => !isOldUnplanned(p));
    }
    filtered.sort((a, b) => {
      const ra = parseRange(a.work_dates);
      const rb = parseRange(b.work_dates);
      if (!ra && !rb) return 0;
      if (!ra) return 1;
      if (!rb) return -1;
      return ra.start.getTime() - rb.start.getTime();
    });
    return filterProjects(filtered, search);
  }, [allItems, filterFromIso, filterToIso, includeOldUnplanned, search]);

  // Re-aggregate KPI по отфильтрованным
  const kpi = useMemo(() => {
    const total_contract = items.reduce((s, p) => s + (p.sum_contract ?? 0), 0);
    const total_actual = items.reduce((s, p) => s + (p.sum_actual ?? 0), 0);
    const total_expected = Math.max(0, total_contract - total_actual);
    const contracts = items.map(p => p.sum_contract).filter((x): x is number => !!x);
    const avg_contract = contracts.length ? Math.round(contracts.reduce((a, b) => a + b, 0) / contracts.length) : 0;
    const cnt_in_progress = items.filter(p => p.status === 'in_progress').length;
    const cnt_awaiting = items.filter(p => p.status === 'awaiting').length;
    const cnt_completed = items.filter(p => p.status === 'completed').length;
    return { total_contract, total_expected, total_actual, clients_count: items.length, avg_contract, cnt_in_progress, cnt_awaiting, cnt_completed };
  }, [items]);

  if (q.isLoading) return <Spinner size="lg" label="Загрузка проектов…"/>;

  return (
    <div className="page">
      {/* KPI */}
      <div className="row row-5">
        <div className="kpi">
          <div className="kpi-label">Сумма договоров</div>
          <div className="kpi-value">{fmt.rub(kpi.total_contract)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Фактическая выручка</div>
          <div className="kpi-value">{fmt.rub(kpi.total_actual)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Ожидаемая выручка</div>
          <div className="kpi-value">{fmt.rub(kpi.total_expected)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Всего клиентов</div>
          <div className="kpi-value">{kpi.clients_count}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Средний чек</div>
          <div className="kpi-value">{fmt.rub(kpi.avg_contract)}</div>
        </div>
      </div>
      <div className="row row-3" style={{ marginTop: 'var(--gap)' }}>
        <div className="kpi">
          <div className="kpi-label">В работе</div>
          <div className="kpi-value">{kpi.cnt_in_progress}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">В ожидании</div>
          <div className="kpi-value">{kpi.cnt_awaiting}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Выполнено</div>
          <div className="kpi-value">{kpi.cnt_completed}</div>
        </div>
      </div>

      {/* Фильтры — строка 1: статусы */}
      <div className="filter-bar" style={{ marginBottom: 8, flexWrap: "wrap" }}>
        <button className={`chip ${!filterStatus ? "active" : ""}`} onClick={() => setFilterStatus("")}>Все статусы</button>
        {STATUS_OPTIONS.map(([id, label]) => (
          <button key={id} className={`chip ${filterStatus === id ? "active" : ""}`} onClick={() => setFilterStatus(filterStatus === id ? "" : id)}>{label}</button>
        ))}
        {oldUnplannedCount > 0 && (
          <button
            className={`chip ${includeOldUnplanned ? "active" : ""}`}
            onClick={() => setIncludeOldUnplanned(v => !v)}
            title="Сделки, созданные в Amo до 1 сентября 2025 и отсутствовавшие в Excel-планировании. По умолчанию исключены из статистики."
            style={includeOldUnplanned ? undefined : { background: "rgba(247,224,138,0.5)", borderColor: "#d4a73a" }}
          >
            {includeOldUnplanned ? "✓ " : ""}Показать старые сделки ({oldUnplannedCount})
          </button>
        )}
      </div>

      {/* Строка 2: период + view-toggle + действия — всё подряд слева */}
      <div className="filter-bar" style={{ marginBottom: "var(--gap)", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        <div className="prod-period-filter">
          <span className="prod-period-label">Период работ</span>
          <input
            type="date"
            className="input input-sm"
            value={filterFromIso}
            onChange={(e) => setFilterFromIso(e.target.value)}
            title="Дата начала диапазона"
          />
          <span style={{ color: "var(--mute)" }}>—</span>
          <input
            type="date"
            className="input input-sm"
            value={filterToIso}
            onChange={(e) => setFilterToIso(e.target.value)}
            title="Дата конца диапазона"
          />
          {(filterFromIso || filterToIso) && (
            <button className="btn btn-sm" onClick={() => { setFilterFromIso(""); setFilterToIso(""); }} title="Сбросить">
              <Icon name="x" size={11}/>
            </button>
          )}
        </div>
        <div className="view-toggle">
          <button
            type="button"
            className={`view-toggle-btn${viewMode === "kanban" ? " is-active" : ""}`}
            onClick={() => changeView("kanban")}
            title="Канбан"
          >
            <Icon name="folder" size={14}/>
          </button>
          <button
            type="button"
            className={`view-toggle-btn${viewMode === "rows" ? " is-active" : ""}`}
            onClick={() => changeView("rows")}
            title="Построчно"
          >
            <Icon name="filter" size={14}/>
          </button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
          {searchOpen || search ? (
            <>
              <input
                type="search"
                autoFocus
                className="input input-sm"
                placeholder="Поиск по клиенту / локации / тегам…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onBlur={() => { if (!search) setSearchOpen(false); }}
                style={{ width: 280, maxWidth: "100%" }}
              />
              <button type="button" className="btn btn-sm btn-ghost"
                      onClick={() => { setSearch(""); setSearchOpen(false); }}
                      title="Закрыть поиск">×</button>
            </>
          ) : (
            <button type="button" className="btn btn-sm" onClick={() => setSearchOpen(true)} title="Поиск">
              <Icon name="search" size={12}/>
            </button>
          )}
        </div>
        <button className="btn btn-sm" onClick={() => syncMut.mutate()} disabled={syncMut.isPending}>
          <Icon name="refresh" size={11}/> {syncMut.isPending ? "…" : "Подтянуть из AmoCRM"}
        </button>
        <button className="btn btn-sm btn-primary" onClick={() => setCreatingNew(true)}>
          <Icon name="plus" size={11}/> Добавить вручную
        </button>
      </div>

      {/* Карточки */}
      {items.length === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "var(--mute)", padding: 40 }}>
          {(filterFromIso || filterToIso || filterStatus)
            ? "По выбранным фильтрам ничего не найдено"
            : "Пока нет проектов. Нажмите «Подтянуть из AmoCRM» или «Добавить вручную»."}
        </div>
      ) : viewMode === "kanban" ? (
        <KanbanView
          items={items}
          creatingNew={creatingNew}
          onCancelCreate={() => setCreatingNew(false)}
          onCreate={(data) => createMut.mutate(data)}
          isCreating={createMut.isPending}
          expandedId={expandedId}
          onToggleExpand={(id) => setExpandedId(expandedId === id ? null : id)}
          onSaved={() => { setExpandedId(null); qc.invalidateQueries({ queryKey: ["production"] }); }}
        />
      ) : (
        <div
          className={`prod-grid prod-view-${viewMode}`}
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          {creatingNew && (
            <ProjectCard
              key="__new"
              p={null}
              expanded={true}
              viewMode={viewMode}
              onToggle={() => setCreatingNew(false)}
              onSaved={() => { setCreatingNew(false); qc.invalidateQueries({ queryKey: ["production"] }); }}
              onCreate={(data) => createMut.mutate(data)}
              isPending={createMut.isPending}
            />
          )}
          {items.map(p => (
            <ProjectCard
              key={p.id}
              p={p}
              expanded={expandedId === p.id}
              viewMode={viewMode}
              onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
              onSaved={() => { setExpandedId(null); qc.invalidateQueries({ queryKey: ["production"] }); }}
            />
          ))}
        </div>
      )}
      {syncDiffs && (
        <SyncDiffsModal
          diffs={syncDiffs}
          busy={applyDiffsMut.isPending}
          onCancel={() => setSyncDiffs(null)}
          onApply={(items) => applyDiffsMut.mutate(items)}
        />
      )}
    </div>
  );
}


/* ── Компактная карточка для канбана. Фиксированная высота — все карточки одинаковы. ── */
const PROD_DRAG_MIME = "application/x-kb-prod-card";

function KanbanCard({ p }: { p: ProductionProject }) {
  const navigate = useNavigate();
  const [dragging, setDragging] = useState(false);
  const fact = p.sum_actual ?? 0;
  const expect = p.sum_expected ?? 0;
  const pct = expect > 0 ? Math.min(100, Math.round((fact / expect) * 100)) : 0;
  const open = () => void navigate({ to: "/production/$projectId" as "/", params: { projectId: String(p.id) } as never });

  function onDragStart(e: React.DragEvent<HTMLDivElement>) {
    const payload = JSON.stringify({ id: p.id, fromStatus: p.status });
    e.dataTransfer.setData(PROD_DRAG_MIME, payload);
    e.dataTransfer.setData("text/plain", payload);
    e.dataTransfer.effectAllowed = "move";
    setDragging(true);
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={() => setDragging(false)}
      onClick={open}
      style={{
        // TG-bubble стиль
        background: "#ffffff",
        border: "1px solid #e6e6e6",
        borderRadius: 12,
        padding: "12px 14px",
        cursor: "grab",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        transition: "border-color 120ms, box-shadow 120ms, transform 120ms, opacity 120ms",
        minHeight: 180,
        boxSizing: "border-box",
        opacity: dragging ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#4fae4e";
        e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e6e6e6";
        e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.06)";
        e.currentTarget.style.transform = "none";
      }}
    >
      <div style={{
        fontSize: 14, fontWeight: 700, color: "var(--ink)", lineHeight: 1.25,
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        minHeight: 35,
      }}>
        {p.client_name}
      </div>
      <div style={{ fontSize: 12, color: "var(--mute)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minHeight: 15 }}>
        {p.location ? `📍 ${p.location}` : " "}
      </div>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "baseline",
        fontSize: 13, paddingTop: 4, borderTop: "1px dotted var(--line)",
      }}>
        <div>
          <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>Договор</div>
          <div style={{ fontWeight: 700, color: "var(--ink-2)", fontVariantNumeric: "tabular-nums" }}>
            {fmt.rub(p.sum_contract ?? 0)}
          </div>
        </div>
        {expect > 0 && (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>Получено</div>
            <div style={{
              fontWeight: 700, fontVariantNumeric: "tabular-nums",
              color: pct === 100 ? "var(--green)" : pct >= 50 ? "#a85f12" : "var(--brick)",
            }}>{pct}%</div>
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, minHeight: 18 }}>
        {[p.brick_color, p.gazebo_size].filter(Boolean).map((t, i) => (
          <span key={i} style={{
            fontSize: 10.5, padding: "2px 8px", borderRadius: 10,
            background: "var(--paper-2)", color: "var(--ink-3)",
            border: "1px solid var(--line)",
          }}>{t}</span>
        ))}
      </div>
      <div style={{ fontSize: 11, color: "var(--mute)", minHeight: 14 }}>
        {p.work_dates || " "}
      </div>
    </div>
  );
}


/* ── Kanban-view: 3 столбца по статусу (В ожидании / В работе / Реализовано) ── */
function KanbanView({
  items, creatingNew, onCancelCreate, onCreate, isCreating,
  expandedId, onToggleExpand, onSaved,
}: {
  items: ProductionProject[];
  creatingNew: boolean;
  onCancelCreate: () => void;
  onCreate: (data: ProductionPatch) => void;
  isCreating: boolean;
  expandedId: number | null;
  onToggleExpand: (id: number) => void;
  onSaved: () => void;
}) {
  void expandedId; void onToggleExpand; void onSaved;
  const qc = useQueryClient();
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const COLUMNS: Array<{
    key: string;
    label: string;
    accent: string;
    statuses: string[];
    /** Конкретный status, в который улетает карточка при drop. */
    targetStatus: string;
  }> = [
    // TG-friendly палитра: серый (ожидание) → синий (в работе) → зелёный (готово)
    { key: "awaiting",    label: "В ожидании",   accent: "#9e9e9e",   statuses: ["awaiting", "not_signed"], targetStatus: "awaiting" },
    { key: "in_progress", label: "В работе",     accent: "#3390ec",   statuses: ["in_progress"],            targetStatus: "in_progress" },
    { key: "completed",   label: "Реализовано",  accent: "#4fae4e",   statuses: ["completed"],              targetStatus: "completed" },
  ];

  const moveMut = useMutation({
    mutationFn: (vars: { id: number; status: string }) =>
      updateProductionProject(vars.id, { status: vars.status as ProductionPatch["status"] }),
    onMutate: async (vars) => {
      // Оптимистично — карточка сразу переезжает, не дожидаясь сервера.
      await qc.cancelQueries({ queryKey: ["production"] });
      const previous = qc.getQueriesData<{ items?: ProductionProject[] } | ProductionProject[]>({ queryKey: ["production"] });
      qc.setQueriesData<{ items?: ProductionProject[] } | ProductionProject[] | undefined>(
        { queryKey: ["production"] },
        (old) => {
          if (!old) return old;
          const apply = (arr: ProductionProject[]) =>
            arr.map(it => it.id === vars.id ? { ...it, status: vars.status } : it);
          if (Array.isArray(old)) return apply(old);
          if (old.items) return { ...old, items: apply(old.items) };
          return old;
        },
      );
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      // Откатываем при ошибке
      ctx?.previous?.forEach(([k, v]) => qc.setQueryData(k, v));
      alert("Не удалось перенести сделку — откатываю.");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["production"] }),
  });

  function handleDrop(e: React.DragEvent<HTMLDivElement>, targetStatus: string) {
    e.preventDefault();
    setDropTarget(null);
    let raw = e.dataTransfer.getData(PROD_DRAG_MIME);
    if (!raw) raw = e.dataTransfer.getData("text/plain");
    if (!raw) return;
    try {
      const payload = JSON.parse(raw) as { id: number; fromStatus: string };
      if (!payload.id || payload.fromStatus === targetStatus) return;
      moveMut.mutate({ id: payload.id, status: targetStatus });
    } catch { /* ignore */ }
  }

  const grouped: Record<string, ProductionProject[]> = { awaiting: [], in_progress: [], completed: [] };
  for (const p of items) {
    const col = COLUMNS.find(c => c.statuses.includes(p.status))?.key;
    if (col) grouped[col].push(p);
  }

  return (
    <div
      className="prod-kanban"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, minmax(300px, 1fr))",
        gap: 14,
        alignItems: "start",
        overflowX: "auto",
        paddingBottom: 12,
      }}
    >
      {COLUMNS.map((col) => {
        const colItems = grouped[col.key] ?? [];
        const totalContract = colItems.reduce((s, p) => s + (p.sum_contract || 0), 0);
        return (
          <div
            key={col.key}
            className="kanban-col"
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropTarget !== col.key) setDropTarget(col.key); }}
            onDragLeave={(e) => {
              // Гасим только если ушли из колонки полностью (не на child)
              if (e.currentTarget.contains(e.relatedTarget as Node)) return;
              if (dropTarget === col.key) setDropTarget(null);
            }}
            onDrop={(e) => handleDrop(e, col.targetStatus)}
            style={{
              background: dropTarget === col.key ? "#e8f5e9" : "#f4f4f5",
              border: `1px solid ${dropTarget === col.key ? col.accent : "#e6e6e6"}`,
              borderTop: `3px solid ${col.accent}`,
              borderRadius: 14,
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 8,
              minHeight: 240,
              transition: "background 100ms, border-color 100ms",
            }}
          >
            <div
              className="kanban-col-header"
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                padding: "4px 6px 8px",
                borderBottom: "1px dotted var(--line)",
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>
                  {col.label}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "#222" }}>
                  {colItems.length}
                </div>
              </div>
              {totalContract > 0 && (
                <div style={{ fontSize: 11, color: "var(--mute)", textAlign: "right" }}>
                  {fmt.rub(totalContract)}
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {col.key === "awaiting" && creatingNew && (
                <ProjectCard
                  key="__new"
                  p={null}
                  expanded={true}
                  viewMode="tiles"
                  onToggle={onCancelCreate}
                  onSaved={() => { onCancelCreate(); onSaved(); }}
                  onCreate={onCreate}
                  isPending={isCreating}
                />
              )}
              {colItems.length === 0 ? (
                <div style={{ color: "var(--mute)", fontSize: 12, textAlign: "center", padding: 20 }}>
                  пусто
                </div>
              ) : (
                colItems.map((p) => <KanbanCard key={p.id} p={p}/>)
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


interface ProjectCardProps {
  p: ProductionProject | null;       // null когда «новая» карточка
  expanded: boolean;
  viewMode: "tiles" | "rows";
  onToggle: () => void;
  onSaved: () => void;
  /** Только для new-карточки — иначе update идёт по id внутри. */
  onCreate?: (data: ProductionPatch) => void;
  isPending?: boolean;
}

function ProjectCard({ p, expanded, viewMode, onToggle, onSaved, onCreate, isPending: createPending }: ProjectCardProps) {
  const navigate = useNavigate();
  // p === null → создаём новую (always expanded)
  const isNew = p === null;
  function goToDetail(e: React.MouseEvent) {
    if (!p) return;
    // Игнорируем клики по интерактивным элементам внутри
    const target = e.target as HTMLElement;
    if (target.closest("a, button, input, select, textarea")) return;
    e.preventDefault();
    void navigate({ to: "/production/$projectId" as "/", params: { projectId: String(p.id) } as never });
  }
  const fact = p?.sum_actual ?? 0;
  const expect = p?.sum_expected ?? 0;
  const pct = expect > 0 ? Math.min(100, Math.round((fact / expect) * 100)) : 0;
  // Состояние формы редактирования (создаётся при expand)
  const [data, setData] = useState<ProductionPatch>(p ? {
    client_name: p.client_name, client_amo_url: p.client_amo_url,
    period_label: p.period_label, status: p.status, work_dates: p.work_dates,
    sum_contract: p.sum_contract, sum_expected: p.sum_expected, sum_actual: p.sum_actual,
    payment_type: p.payment_type, location: p.location,
    what_done: p.what_done, brick_color: p.brick_color, brick_count: p.brick_count,
    gazebo_size: p.gazebo_size, accommodation: p.accommodation,
    water_source: p.water_source, needs_pump: p.needs_pump,
    comment: p.comment,
  } : { status: "in_progress" });

  // Сброс формы когда expand закрывается / открывается на другом проекте
  // (используем p.id и expanded в effect)
  // Без useEffect: при expanded=false ниже просто не рендерим форму
  // → следующий expand создаст компонент заново и стейт инициализируется снова

  const save = useMutation({
    mutationFn: () => p ? updateProductionProject(p.id, data) : Promise.reject("no project"),
    onSuccess: onSaved,
    onError: (e: Error) => alert(`Ошибка: ${e.message}`),
  });
  const del = useMutation({
    mutationFn: () => p ? deleteProductionProject(p.id) : Promise.reject("no project"),
    onSuccess: onSaved,
  });
  // Quick-toggle payment type на бейдже карточки (без раскрытия)
  const paymentToggle = useMutation({
    mutationFn: () => p
      ? updateProductionProject(p.id, { payment_type: nextPaymentType(p.payment_type) })
      : Promise.reject("no project"),
    onSuccess: onSaved,
  });

  function set<K extends keyof ProductionPatch>(k: K, v: ProductionPatch[K]) {
    setData(d => ({ ...d, [k]: v }));
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (isNew && onCreate) {
      onCreate(data);
    } else {
      save.mutate();
    }
  }

  // Click на карточку = переход на детальную страницу (вместо inline-раскрытия)
  function handleCardClick(e: React.MouseEvent) {
    if (isNew) return;  // новая карточка — открыта в форме создания
    goToDetail(e);
  }

  // Row-layout — компактная строка вместо плитки (клик ведёт на детальную страницу)
  if (viewMode === "rows" && !expanded && !isNew && p) {
    return (
      <div className={`prod-row${p && p.lead_created_at && p.lead_created_at < "2025-09-01" && !p.in_planning ? " prod-card-old-unplanned" : ""}`}
           onClick={goToDetail} style={{ cursor: "pointer" }}
           title={p && p.lead_created_at && p.lead_created_at < "2025-09-01" && !p.in_planning ? "Старая сделка (до сентября 2025) и не в Excel-планировании" : undefined}>
        <div className="prod-row-name">
          <div style={{ fontWeight: 600, fontSize: 14 }}>{p.client_name}</div>
          {p.location && <div style={{ fontSize: 11.5, color: "var(--mute)" }}>📍 {p.location}</div>}
        </div>
        <div className="prod-row-period">
          {p.period_label && <span style={{ fontWeight: 600 }}>{p.period_label}</span>}
        </div>
        <div className="prod-row-dates">
          {p.work_dates && <span>{p.work_dates}</span>}
        </div>
        <div className="prod-row-money">
          <div title="Сумма договора">{fmt.rub(p.sum_contract ?? 0)}</div>
          <div style={{ fontSize: 11, color: pct === 100 ? "var(--green)" : "var(--brick)" }} title="Получено">
            {fmt.rub(fact)} · {pct}%
          </div>
        </div>
        <span className="prod-row-status" style={{ background: statusColor(p.status) }}>
          {statusLabel(p.status)}
        </span>
        {p.client_amo_url ? (
          <a className="btn btn-sm" href={p.client_amo_url} target="_blank" rel="noopener" title="Открыть в AmoCRM">
            <Icon name="external" size={11}/>
          </a>
        ) : (
          <span style={{ opacity: 0.25, display: "grid", placeItems: "center" }} title="Нет ссылки на AmoCRM">
            <Icon name="external" size={11}/>
          </span>
        )}
        <Icon name="chevron_r" size={14}/>
      </div>
    );
  }

  // Старая сделка вне планирования — желтый фон.
  // «Старая» = lead.created_at в Amo < 2025-09-01, и НЕТ в Excel-планировании.
  const isOldUnplanned = !!p && !!p.lead_created_at
    && p.lead_created_at < "2025-09-01" && !p.in_planning;
  return (
    <div
      className={`prod-card${expanded ? " is-expanded" : ""}${isOldUnplanned ? " prod-card-old-unplanned" : ""}`}
      onClick={!expanded && !isNew ? goToDetail : (!expanded ? handleCardClick : undefined)}
      style={{ cursor: !expanded ? "pointer" : "default" }}
      title={isOldUnplanned ? "Старая сделка (создана до сентября 2025) и не была в Excel-планировании" : undefined}
    >
      {/* Краткая шапка — всегда видна */}
      <div className="prod-card-summary">
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>
              {isNew ? "Новая карточка" : p?.client_name}
            </div>
            {!isNew && p?.location && (
              <div style={{ fontSize: 12.5, color: "var(--mute)", marginTop: 2 }}>📍 {p.location}</div>
            )}
          </div>
          {!isNew && p && (
            <span style={{
              padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600,
              background: statusColor(p.status), color: "var(--ink-3)",
              whiteSpace: "nowrap", flexShrink: 0,
            }}>
              {statusLabel(p.status)}
            </span>
          )}
        </div>

        {!isNew && p && (p.period_label || p.work_dates) && (
          <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 8 }}>
            {p.period_label && <strong>{p.period_label}</strong>}
            {p.period_label && p.work_dates && " · "}
            {p.work_dates && <span>{p.work_dates}</span>}
          </div>
        )}

        {!isNew && p && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 12.5, marginTop: 10 }}>
            <div>
              <div style={{ color: "var(--mute)", fontSize: 11 }}>Договор</div>
              <div style={{ fontFamily: "var(--serif)", fontSize: 16, fontWeight: 700 }}>{fmt.rub(p.sum_contract ?? 0)}</div>
            </div>
            <div>
              <div style={{ color: "var(--mute)", fontSize: 11 }}>Получено</div>
              <div style={{ fontFamily: "var(--serif)", fontSize: 16, fontWeight: 700, color: pct === 100 ? "var(--green)" : "var(--brick)" }}>
                {fmt.rub(fact)} <span style={{ fontSize: 11, color: "var(--mute)", fontFamily: "var(--sans)", fontWeight: 400 }}>· {pct}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Краткий tech-блок только когда не expanded — иначе всё в форме */}
        {!expanded && !isNew && p && (p.brick_color || p.gazebo_size || p.what_done) && (
          <div style={{ fontSize: 12, color: "var(--ink-3)", lineHeight: 1.5, marginTop: 10 }}>
            {p.what_done && <div>✓ {p.what_done}</div>}
            {p.brick_color && <div>🧱 {p.brick_color}{p.brick_count ? ` — ${p.brick_count} шт` : ""}</div>}
            {p.gazebo_size && <div>📐 {p.gazebo_size}</div>}
            {p.accommodation && <div>🏠 {p.accommodation}</div>}
            {p.water_source && <div>💧 {p.water_source}</div>}
            {p.needs_pump && <div>⚙️ Насос: {p.needs_pump}</div>}
          </div>
        )}

        {!expanded && !isNew && p?.comment && (
          <div style={{
            fontSize: 12, color: "var(--ink-3)",
            background: "var(--paper-2)", padding: "6px 10px", borderRadius: 6,
            borderLeft: "2px solid var(--brick)", marginTop: 10,
          }}>
            {p.comment}
          </div>
        )}

        {/* Spacer — поджимает footer вниз карточки */}
        {!expanded && !isNew && <div className="prod-card-spacer"/>}

        {/* Footer: всегда внизу на одной высоте у всех карточек */}
        {!expanded && !isNew && p && (
          <div className="prod-card-footer">
            {p.client_amo_url ? (
              <a className="btn btn-sm" href={p.client_amo_url} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
                <Icon name="external" size={11}/> AmoCRM
              </a>
            ) : (
              <span style={{ width: 1 }}/>
            )}
            <button
              type="button"
              className="badge badge-mute"
              style={{ fontSize: 10, cursor: "pointer", border: "none", padding: "3px 8px" }}
              title="Кликнуть чтобы переключить тип расчёта"
              onClick={(e) => { e.stopPropagation(); paymentToggle.mutate(); }}
            >
              {paymentLabel(p.payment_type)}
            </button>
            <span style={{ marginLeft: "auto", color: "var(--mute)", fontSize: 11 }}>Открыть →</span>
          </div>
        )}
      </div>

      {/* Раскрытая форма редактирования */}
      {expanded && (
        <form onSubmit={onSubmit} className="prod-card-form" onClick={(e) => e.stopPropagation()}>
          <div className="prod-form-row">
            <Field label="Клиент" required>
              <input className="input" value={data.client_name ?? ""} onChange={(e) => set("client_name", e.target.value)} required/>
            </Field>
          </div>
          <div className="prod-form-row prod-form-grid-2">
            <Field label="Период">
              <select className="input" value={data.period_label ?? ""} onChange={(e) => set("period_label", e.target.value || null)}>
                <option value="">— выбрать месяц —</option>
                {PERIOD_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Статус">
              <select className="input" value={data.status ?? "in_progress"} onChange={(e) => set("status", e.target.value)}>
                {STATUS_OPTIONS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Даты работ">
            <DateRangeInput value={data.work_dates ?? ""} onChange={(v) => set("work_dates", v)}/>
          </Field>
          <div className="prod-form-row prod-form-grid-3">
            <Field label="Сумма договора (₽)">
              <MoneyInput value={data.sum_contract} onChange={(v) => set("sum_contract", v)}/>
            </Field>
            <Field label="Ожидаемая (₽)">
              <MoneyInput value={data.sum_expected} onChange={(v) => set("sum_expected", v)}/>
            </Field>
            <Field label="Фактическая (₽)">
              <MoneyInput value={data.sum_actual} onChange={(v) => set("sum_actual", v)}/>
            </Field>
          </div>
          <div className="prod-form-row prod-form-grid-2">
            <Field label="Оплата">
              <select className="input" value={data.payment_type ?? ""} onChange={(e) => set("payment_type", e.target.value || null)}>
                <option value="">—</option>
                <option value="cash">Налички</option>
                <option value="non_cash">Безнал</option>
              </select>
            </Field>
            <Field label="Локация"><input className="input" value={data.location ?? ""} onChange={(e) => set("location", e.target.value)}/></Field>
          </div>
          <Field label="Что уже сделали"><input className="input" value={data.what_done ?? ""} onChange={(e) => set("what_done", e.target.value)}/></Field>
          <div className="prod-form-row prod-form-grid-3">
            <Field label="Цвет кирпича"><input className="input" placeholder="Яшма / Агат" value={data.brick_color ?? ""} onChange={(e) => set("brick_color", e.target.value)}/></Field>
            <Field label="Кол-во шт"><input className="input" type="number" value={data.brick_count ?? ""} onChange={(e) => set("brick_count", e.target.value ? parseInt(e.target.value, 10) : null)}/></Field>
            <Field label="Размер беседки"><input className="input" placeholder="5.0×6.0" value={data.gazebo_size ?? ""} onChange={(e) => set("gazebo_size", e.target.value)}/></Field>
          </div>
          <Field label="Проживание"><input className="input" value={data.accommodation ?? ""} onChange={(e) => set("accommodation", e.target.value)}/></Field>
          <div className="prod-form-row prod-form-grid-2">
            <Field label="Вода"><input className="input" value={data.water_source ?? ""} onChange={(e) => set("water_source", e.target.value)}/></Field>
            <Field label="Насос"><input className="input" value={data.needs_pump ?? ""} onChange={(e) => set("needs_pump", e.target.value)}/></Field>
          </div>
          <Field label="Комментарий">
            <textarea className="input" rows={3} value={data.comment ?? ""} onChange={(e) => set("comment", e.target.value)}/>
          </Field>
          <Field label="Ссылка на AmoCRM"><input className="input" type="url" placeholder="https://kshagin2009.amocrm.ru/leads/detail/…" value={data.client_amo_url ?? ""} onChange={(e) => set("client_amo_url", e.target.value)}/></Field>

          {p?.update_log && p.update_log.length > 0 && (
            <details style={{ marginTop: 12, marginBottom: 8, fontSize: 12 }}>
              <summary style={{ cursor: "pointer", color: "var(--mute)", padding: "6px 0" }}>📜 История изменений из Amo · {p.update_log.length}</summary>
              <div style={{ marginTop: 8, padding: 10, background: "var(--paper-2)", borderRadius: 6 }}>
                {p.update_log.slice().reverse().map((entry, i) => (
                  <div key={i} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: "1px solid var(--line)" }}>
                    <div style={{ color: "var(--mute)", fontSize: 11 }}>{new Date(entry.ts).toLocaleString("ru-RU")} · {entry.source}</div>
                    {Object.entries(entry.fields).map(([k, v]) => (
                      <div key={k} style={{ marginTop: 2 }}>
                        <span style={{ color: "var(--mute)" }}>{k}:</span>{" "}
                        <span style={{ textDecoration: "line-through", color: "var(--mute)" }}>{String((v as { old: unknown }).old ?? "—")}</span>{" → "}
                        <span style={{ color: "var(--brick-soft)" }}>{String((v as { new: unknown }).new ?? "—")}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </details>
          )}

          <div className="prod-form-actions">
            {!isNew && p && (
              <button type="button" className="btn btn-sm" style={{ color: "var(--brick)", borderColor: "var(--brick)" }}
                onClick={() => { if (confirm("Удалить карточку?")) del.mutate(); }}>
                <Icon name="trash" size={11}/> Удалить
              </button>
            )}
            <button type="button" className="btn btn-sm" onClick={onToggle} style={{ marginLeft: "auto" }}>
              ↑ Свернуть
            </button>
            <button type="submit" className="btn btn-sm btn-primary" disabled={save.isPending || createPending}>
              {(save.isPending || createPending) ? "…" : isNew ? "Создать" : "Сохранить"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}


/** Сумма с авто-форматированием через тонкий пробел: 2530000 → 2 530 000 */
function MoneyInput({ value, onChange }: { value: number | null | undefined; onChange: (v: number | null) => void }) {
  const display = value !== null && value !== undefined && !Number.isNaN(value)
    ? new Intl.NumberFormat("ru-RU").format(value)
    : "";
  return (
    <input
      className="input"
      type="text"
      inputMode="numeric"
      value={display}
      placeholder="0"
      onChange={(e) => {
        const raw = e.target.value.replace(/[^\d]/g, "");
        onChange(raw ? parseInt(raw, 10) : null);
      }}
    />
  );
}

/** Range из двух date-input'ов. Хранит как строку «DD.MM.YYYY — DD.MM.YYYY». */
function DateRangeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  // Парсим текущую строку
  const re = /(\d{2}\.\d{2}\.\d{4})\s*[—\-–]\s*(\d{2}\.\d{2}\.\d{4})/;
  const match = value.match(re);
  const toIso = (ddmmyyyy: string): string => {
    const [d, m, y] = ddmmyyyy.split(".");
    return y && m && d ? `${y}-${m}-${d}` : "";
  };
  const fromIso = (iso: string): string => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}.${m}.${y}`;
  };
  const fromISO = match ? toIso(match[1]) : "";
  const toISO = match ? toIso(match[2]) : "";

  function update(newFrom: string, newTo: string) {
    if (newFrom && newTo) {
      onChange(`${fromIso(newFrom)} — ${fromIso(newTo)}`);
    } else if (newFrom) {
      onChange(`${fromIso(newFrom)} — `);
    } else if (newTo) {
      onChange(` — ${fromIso(newTo)}`);
    } else {
      onChange("");
    }
  }

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <input
        className="input"
        type="date"
        value={fromISO}
        onChange={(e) => update(e.target.value, toISO)}
        style={{ flex: 1 }}
      />
      <span style={{ color: "var(--mute)" }}>—</span>
      <input
        className="input"
        type="date"
        value={toISO}
        onChange={(e) => update(fromISO, e.target.value)}
        style={{ flex: 1 }}
      />
    </div>
  );
}


function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {label}{required && <span style={{ color: "var(--brick)" }}> *</span>}
      </div>
      {children}
    </label>
  );
}

export default ProductionPage;


interface ApplyItem {
  pp_id: number;
  fields: { name: "client_name" | "sum_contract" | "sum_expected"; new: string | number | null }[];
  propagate_to_po?: boolean;
}

function SyncDiffsModal({
  diffs, busy, onCancel, onApply,
}: {
  diffs: SyncPendingDiff[];
  busy: boolean;
  onCancel: () => void;
  onApply: (items: ApplyItem[]) => void;
}) {
  const [selected, setSelected] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    for (const d of diffs) for (const k of Object.keys(d.fields)) init[`${d.pp_id}:${k}`] = true;
    return init;
  });
  const [propagatePo, setPropagatePo] = useState(true);

  function toggle(pp_id: number, field: string) {
    const k = `${pp_id}:${field}`;
    setSelected(s => ({ ...s, [k]: !s[k] }));
  }

  function build() {
    const items: ApplyItem[] = [];
    for (const d of diffs) {
      const fields: ApplyItem["fields"] = [];
      for (const [k, v] of Object.entries(d.fields)) {
        if (!selected[`${d.pp_id}:${k}`]) continue;
        fields.push({ name: k as ApplyItem["fields"][number]["name"], new: (v as { new: string | number | null }).new });
      }
      if (fields.length) items.push({ pp_id: d.pp_id, fields, propagate_to_po: propagatePo });
    }
    onApply(items);
  }

  const totalChosen = Object.values(selected).filter(Boolean).length;
  const fmtVal = (k: string, v: unknown) => {
    if (v == null || v === "") return "—";
    if (k === "sum_contract" || k === "sum_expected") {
      const n = typeof v === "number" ? v : Number(v);
      return Number.isFinite(n) ? `${n.toLocaleString("ru-RU")} ₽` : String(v);
    }
    return String(v);
  };
  const labels: Record<string, string> = {
    client_name: "Имя клиента",
    sum_contract: "Сумма договора",
    sum_expected: "Ожидаемая сумма",
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal-window" style={{ maxWidth: 880, maxHeight: "85vh" }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Изменения в Amo · {diffs.length} карточек</h3>
          <button className="icon-btn" onClick={onCancel} aria-label="Закрыть"><Icon name="x"/></button>
        </div>
        <div className="modal-body" style={{ overflowY: "auto", flex: 1 }}>
          <div style={{ marginBottom: 12, fontSize: 13, color: "var(--mute)" }}>
            Найдены отличия между AmoCRM и Производством. Отметь те, что хочешь применить.
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontSize: 13 }}>
            <input type="checkbox" checked={propagatePo} onChange={e => setPropagatePo(e.target.checked)}/>
            <span>Применять также к парным объектам в P&L (имя/сумма договора)</span>
          </label>
          <table className="tbl" style={{ width: "100%", fontSize: 13 }}>
            <thead><tr>
              <th style={{ textAlign: "left" }}>Карточка</th>
              <th style={{ textAlign: "left" }}>Поле</th>
              <th style={{ textAlign: "left" }}>Сейчас</th>
              <th style={{ textAlign: "left" }}>Из Amo</th>
              <th></th>
            </tr></thead>
            <tbody>
              {diffs.flatMap(d => Object.entries(d.fields).map(([k, v]) => {
                const fld = v as { old: unknown; new: unknown };
                const key = `${d.pp_id}:${k}`;
                return (
                  <tr key={key}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{d.current_name || `pp ${d.pp_id}`}</div>
                      {d.amo_url && <a href={d.amo_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--mute)" }}>Amo →</a>}
                    </td>
                    <td>{labels[k] || k}</td>
                    <td style={{ color: "var(--mute)" }}>{fmtVal(k, fld.old)}</td>
                    <td style={{ color: "var(--brick-soft)" }}>{fmtVal(k, fld.new)}</td>
                    <td><input type="checkbox" checked={!!selected[key]} onChange={() => toggle(d.pp_id, k)}/></td>
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
        <div className="modal-footer" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>Отмена</button>
          <button className="btn btn-primary" onClick={build} disabled={busy || totalChosen === 0}>
            Применить ({totalChosen})
          </button>
        </div>
      </div>
    </div>
  );
}
